import { useState, startTransition, useEffect, Children } from "react"
import { motion } from "framer-motion"
import styled from "@emotion/styled"

import { useDictionary } from "contexts/Dictionary"
import { useConsole } from "contexts/Console"
import { contrastMode } from "css/a11y"
import getMediaQuery from "css/breakpoints"

import { useRoller } from "./contexts"

const MarkerContainer = styled.div`
  height: fit-content;
  pointer-events: none;
  position: absolute;
  bottom: 2.5rem;
  z-index: 1;
  width: 100vw;

  ${getMediaQuery("m")} {
    bottom: 5rem;
  }

  & > div {
    display: flex;
    width: 100%;
    align-items: center;
    justify-content: center;
  }

  & > div > div {
    width: fit-content;
    display: grid;
    grid-template-columns: auto;
    grid-auto-flow: column;
    flex-direction: row;
    position: relative;
    ${contrastMode}
  }
`

const Mark = styled(motion.button)`
  height: 0.25rem;
  border-radius: 9999px;
  background: var(--bg);
  border: none;
  padding: 0;
  pointer-events: all;
  position: relative;
  margin-inline: 0.375rem;

  ${getMediaQuery("m")} {
    background: white;
  }

  ::after {
    content: "";
    position: absolute;
    inset: -12px -4px;
  }
  html.prefers-contrast & {
    background: black;
  }
`

const variants = {
  active: { width: "6.25rem", cursor: "default" },
  inactive: { width: "2.5rem", cursor: "pointer" },
}

function Marker({ index, totalMarkers }) {
  const console = useConsole()
  const dictionary = useDictionary()

  const { currentIndex } = useRoller()
  const [active, setActive] = useState(currentIndex.get() === index)
  const isLastIndex = currentIndex.get() === totalMarkers - 1

  const onClick = () => {
    if (active) return
    currentIndex.set(index)
  }

  function onIndexChange(i) {
    startTransition(() => setActive(i === index))
  }
  useEffect(() => currentIndex.onChange(onIndexChange))

  console.verbose("Marker(%o)", { index, active })
  return (
    <Mark
      onClick={onClick}
      style={{ "--bg": isLastIndex ? "rgb(var(--light-black))" : "rgb(var(--pure-white))" }}
      variants={variants}
      animate={active ? "active" : "inactive"}
      initial={active ? "active" : "inactive"}
      transition={{ type: "tween", ease: "easeOut", duration: 0.3 }}
      aria-label={index === 0 ? dictionary.ariaLabelSeePhoto() : dictionary.ariaLabelSeeDetails()}
    />
  )
}

export default function Markers({ children }) {
  const totalMarkers = children.length
  return (
    <MarkerContainer>
      <div>
        <div>
          {new Array(children?.length ?? 0).fill(null).map((_, i) => (
            <Marker key={`marker-${i}`} totalMarkers={totalMarkers} index={i} />
          ))}
        </div>
      </div>
    </MarkerContainer>
  )
}
